import React from 'react';

import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { formatDateString, formatLongWithTimezone } from '../../../../utils/formatters';
import Paginator from '../../../common/Paginator';
import { Route } from '../../../common/Route';
import { SetStateFunction } from '../../../play/Types/Types';

import { isMML } from 'utils/template';

type Props = {
    readonly news: object[];
    readonly loading: boolean;
    readonly hiddenComponent: boolean;
    readonly displaynewsdate: boolean;
    readonly timezone: string;
    readonly defaultthumbnailsrc: string;
    readonly getAllNews: boolean;
    readonly lastPage: number;
    readonly currentPage: number;
    readonly setCurrentPage: SetStateFunction<number>;
    readonly isNewsImgVisible: boolean;
};

function DefaultTemplate({
    news,
    loading,
    hiddenComponent,
    displaynewsdate,
    timezone,
    defaultthumbnailsrc,
    getAllNews,
    lastPage,
    currentPage,
    setCurrentPage,
    isNewsImgVisible,
}: Props) {
    const { t } = useTranslation();

    function getNews(singleNews, index) {
        const newsUrl = singleNews.slug ? Route('singlenews', { base_news: singleNews.slug }) : '';
        const thumbnailUrl = singleNews.image ? Route('thumbnail', { id: singleNews.id }) : defaultthumbnailsrc;

        return (
            <article key={`news-${index}`}>
                <div className="row no-gutters">
                    {isNewsImgVisible && (
                        <div className={isMML ? 'col-3 col-md-2 article-img' : 'col-3 col-md-2 d-lg-none article-img'}>
                            <a href={newsUrl} className="d-block">
                                {loading ? (
                                    <Skeleton />
                                ) : (
                                    <img
                                        loading="lazy"
                                        src={thumbnailUrl}
                                        alt={t('news.newsImage')}
                                        className="img-fluid"
                                        data-dusk={`news_img-${index + 1}`}
                                    />
                                )}
                            </a>
                        </div>
                    )}
                    <div className={isNewsImgVisible ? 'col article-content' : 'article-content px-5 px-md-0'}>
                        <a href={newsUrl}>
                            <h2 className="card-title">{singleNews.title || (loading && <Skeleton />)}</h2>
                        </a>
                        {displaynewsdate && (
                            <time dateTime={formatDateString(singleNews.created_at)} className="card-subtitle date">
                                {formatLongWithTimezone(singleNews.created_at, timezone)}
                            </time>
                        )}
                        <p className="card-text">{singleNews.short_text || (loading && <Skeleton count={3} />)}</p>

                        {singleNews.slug ? (
                            <a href={newsUrl} className="card-link font-bold">
                                {t('news.readMore')} &#8594;
                            </a>
                        ) : (
                            <Skeleton />
                        )}
                    </div>
                </div>
            </article>
        );
    }

    return (
        <div className={hiddenComponent ? 'd-none' : 'container news skeleton-container'}>
            <div className="col">
                <div className="col-8 d-lg-flex d-none news-bg mb-5" />
                <div className="news-articles">{news.map((singleNews, index) => getNews(singleNews, index))}</div>

                {getAllNews && (
                    <Paginator currentPage={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} />
                )}
            </div>
        </div>
    );
}

export default DefaultTemplate;
